import React, { useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  Container, Box, Typography, CircularProgress, Alert, Breadcrumbs, Link,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ModelSection from '../components/ModelSection'
import ResourceList from '../components/ResourceList'
import { fetchProduct } from '../api/catalogClient'

const NOT_AVAILABLE = 'Цей матеріал недоступний.'
const ROOT_LABEL = 'Ресурси'

function NotAvailable() {
  return <Container sx={{ py: 4 }}><Alert severity="error">{NOT_AVAILABLE}</Alert></Container>
}

function BackLink({ to, label }) {
  return (
    <Link
      component={RouterLink}
      to={to}
      underline="hover"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1 }}
    >
      <ArrowBackIcon fontSize="small" />
      {label}
    </Link>
  )
}

function Crumbs({ items }) {
  return (
    <Breadcrumbs separator="·" sx={{ mb: 1 }}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        if (isLast || !item.to) {
          return (
            <Typography key={index} variant="overline" color="text.secondary">
              {item.label}
            </Typography>
          )
        }
        return (
          <Link
            key={index}
            component={RouterLink}
            to={item.to}
            underline="hover"
            variant="overline"
            color="text.secondary"
          >
            {item.label}
          </Link>
        )
      })}
    </Breadcrumbs>
  )
}

// Renders one product, model or version of the public catalog. `scope` selects
// how deep into the fetched product tree to slice. Only published items are
// reachable, since the API only ever returns the published tree.
export default function DetailPage({ scope }) {
  const { productSlug, modelSlug, versionSlug } = useParams()
  const [product, setProduct] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    setProduct(null)
    setError(null)
    fetchProduct(productSlug)
      .then((data) => { if (active) setProduct(data) })
      .catch(() => { if (active) setError(NOT_AVAILABLE) })
    return () => { active = false }
  }, [productSlug])

  if (error) return <NotAvailable />

  if (!product) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (scope === 'product') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackLink to="/" label={ROOT_LABEL} />
        <Crumbs items={[{ label: ROOT_LABEL, to: '/' }, { label: product.name }]} />
        <Typography variant="h4" gutterBottom>{product.name}</Typography>
        {product.description && (
          <Typography color="text.secondary" sx={{ mb: 3 }}>{product.description}</Typography>
        )}
        {product.models.length === 0 ? (
          <Typography color="text.secondary">
            Для цього продукту ще немає опублікованих моделей.
          </Typography>
        ) : (
          product.models.map((model) => <ModelSection key={model.id} model={model} />)
        )}
      </Container>
    )
  }

  const model = product.models.find((m) => m.slug === modelSlug)
  if (!model) return <NotAvailable />

  if (scope === 'model') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <BackLink to={`/${product.slug}`} label={product.name} />
        <Crumbs
          items={[
            { label: ROOT_LABEL, to: '/' },
            { label: product.name, to: `/${product.slug}` },
            { label: model.name },
          ]}
        />
        <ModelSection model={model} />
      </Container>
    )
  }

  // scope === 'version'
  const version = model.versions.find((v) => v.slug === versionSlug)
  if (!version) return <NotAvailable />

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackLink to={`/${product.slug}/${model.slug}`} label={model.name} />
      <Crumbs
        items={[
          { label: ROOT_LABEL, to: '/' },
          { label: product.name, to: `/${product.slug}` },
          { label: model.name, to: `/${product.slug}/${model.slug}` },
          { label: version.name },
        ]}
      />
      <Typography variant="h4" gutterBottom>{version.name}</Typography>
      {version.description && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>{version.description}</Typography>
      )}
      <ResourceList resources={version.resources} />
    </Container>
  )
}
